/** Local-first hub store. Swap-ready for a later API. Keys: dh_hub_*. */

import { HUB_PREFIX } from "./constants.js";
import {
  SEED_ARTICLES,
  SEED_BBS_COMMENTS,
  SEED_BBS_POSTS,
  SEED_BLURBS,
  SEED_EVENTS,
  SEED_SETTINGS,
  SEED_VENDORS,
  SEED_VISIBILITY,
} from "./seed.js";

const KEYS = {
  settings: `${HUB_PREFIX}settings`,
  articles: `${HUB_PREFIX}articles`,
  vendors: `${HUB_PREFIX}vendors`,
  events: `${HUB_PREFIX}events`,
  posts: `${HUB_PREFIX}bbs_posts`,
  comments: `${HUB_PREFIX}bbs_comments`,
  likes: `${HUB_PREFIX}bbs_likes`,
  blurbs: `${HUB_PREFIX}blurbs`,
  visibility: `${HUB_PREFIX}visibility`,
  suggestions: `${HUB_PREFIX}suggestions`,
  logins: `${HUB_PREFIX}login_events`,
  followups: `${HUB_PREFIX}followups`,
};

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — do not blank the site */
  }
}

function nowIso() {
  return new Date().toISOString();
}

function newId() {
  return crypto.randomUUID();
}

function seededList(key, seed) {
  const stored = read(key, null);
  if (Array.isArray(stored) && stored.length) return stored;
  write(key, seed);
  return seed;
}

function mergeSeedList(key, seed) {
  const stored = read(key, null);
  if (!Array.isArray(stored) || !stored.length) {
    write(key, seed);
    return seed;
  }
  const have = new Map(stored.map((item) => [item.id, { ...item }]));
  const extra = [];
  seed.forEach((item) => {
    if (!item.id) return;
    if (!have.has(item.id)) {
      extra.push(item);
      return;
    }
    const current = have.get(item.id);
    if (!current.starts_at && item.starts_at) current.starts_at = item.starts_at;
    if (!current.region && item.region) current.region = item.region;
    if (current.audience === "client" && current.group === "escrow") current.group = "escrow_edu";
    have.set(item.id, current);
  });
  const next = [...stored.map((item) => (item.id && have.get(item.id)) || item), ...extra];
  if (JSON.stringify(next) !== JSON.stringify(stored)) write(key, next);
  return next;
}

function seededObject(key, seed) {
  const stored = read(key, null);
  if (stored && typeof stored === "object" && !Array.isArray(stored) && Object.keys(stored).length) {
    return { ...seed, ...stored };
  }
  write(key, seed);
  return { ...seed };
}

export function ensureHub() {
  seededObject(KEYS.settings, SEED_SETTINGS);
  seededObject(KEYS.blurbs, SEED_BLURBS);
  seededObject(KEYS.visibility, SEED_VISIBILITY);
  mergeSeedList(KEYS.articles, SEED_ARTICLES);
  mergeSeedList(KEYS.vendors, SEED_VENDORS);
  mergeSeedList(KEYS.events, SEED_EVENTS);
  seededList(KEYS.posts, SEED_BBS_POSTS);
  seededList(KEYS.comments, SEED_BBS_COMMENTS);
  if (read(KEYS.likes, null) == null) write(KEYS.likes, []);
  if (read(KEYS.suggestions, null) == null) write(KEYS.suggestions, []);
  if (read(KEYS.logins, null) == null) write(KEYS.logins, []);
  if (read(KEYS.followups, null) == null) write(KEYS.followups, []);
  return true;
}

export function exportHubPack() {
  ensureHub();
  return {
    exported_at: nowIso(),
    settings: getSettings(),
    blurbs: getBlurbs(),
    visibility: getVisibility(),
    articles: listArticles(),
    vendors: listVendors(),
    events: listEvents(),
    posts: listPosts(),
    comments: listComments(),
  };
}

export function importHubPack(pack = {}) {
  if (pack.settings) write(KEYS.settings, { ...SEED_SETTINGS, ...pack.settings });
  if (pack.blurbs) write(KEYS.blurbs, { ...SEED_BLURBS, ...pack.blurbs });
  if (pack.visibility) write(KEYS.visibility, { ...SEED_VISIBILITY, ...pack.visibility });
  if (Array.isArray(pack.articles)) write(KEYS.articles, pack.articles);
  if (Array.isArray(pack.vendors)) write(KEYS.vendors, pack.vendors);
  if (Array.isArray(pack.events)) write(KEYS.events, pack.events);
  if (Array.isArray(pack.posts)) write(KEYS.posts, pack.posts);
  if (Array.isArray(pack.comments)) write(KEYS.comments, pack.comments);
  return exportHubPack();
}

export function getSettings() {
  ensureHub();
  return seededObject(KEYS.settings, SEED_SETTINGS);
}

export function saveSettings(patch) {
  const next = { ...getSettings(), ...patch };
  write(KEYS.settings, next);
  return next;
}

export function getBlurbs() {
  ensureHub();
  return seededObject(KEYS.blurbs, SEED_BLURBS);
}

export function saveBlurbs(patch) {
  const next = { ...getBlurbs(), ...patch };
  write(KEYS.blurbs, next);
  return next;
}

export function getVisibility() {
  ensureHub();
  return seededObject(KEYS.visibility, SEED_VISIBILITY);
}

export function saveVisibility(patch) {
  const next = { ...getVisibility(), ...patch };
  write(KEYS.visibility, next);
  return next;
}

function upsertList(key, seed, row) {
  const list = seededList(key, seed);
  const index = list.findIndex((item) => item.id === row.id);
  const next = { ...row, updated_at: nowIso() };
  if (index === -1) list.unshift(next);
  else list[index] = { ...list[index], ...next };
  write(key, list);
  return next;
}

function removeFromList(key, seed, id) {
  const list = seededList(key, seed).filter((item) => item.id !== id);
  write(key, list);
  return list;
}

export function listArticles() {
  ensureHub();
  return mergeSeedList(KEYS.articles, SEED_ARTICLES);
}

export function saveArticle(input = {}) {
  return upsertList(KEYS.articles, SEED_ARTICLES, {
    id: input.id || newId(),
    role: input.role || "buyer",
    section: input.section || "faq",
    title: String(input.title || "").trim(),
    body: String(input.body || "").trim(),
  });
}

export function deleteArticle(id) {
  return removeFromList(KEYS.articles, SEED_ARTICLES, id);
}

export function listVendors() {
  ensureHub();
  return mergeSeedList(KEYS.vendors, SEED_VENDORS);
}

export function saveVendor(input = {}) {
  return upsertList(KEYS.vendors, SEED_VENDORS, {
    id: input.id || newId(),
    audience: input.audience === "pro" ? "pro" : "client",
    group: input.group || "services",
    name: String(input.name || "").trim(),
    summary: String(input.summary || "").trim(),
    region: String(input.region || "").trim(),
    url: String(input.url || "").trim(),
  });
}

export function deleteVendor(id) {
  return removeFromList(KEYS.vendors, SEED_VENDORS, id);
}

export function listEvents() {
  ensureHub();
  return mergeSeedList(KEYS.events, SEED_EVENTS).sort((a, b) =>
    String(a.starts_on).localeCompare(String(b.starts_on))
  );
}

export function saveEvent(input = {}) {
  return upsertList(KEYS.events, SEED_EVENTS, {
    id: input.id || newId(),
    title: String(input.title || "").trim(),
    kind: input.kind || "expo",
    scope: input.scope || "local",
    starts_on: input.starts_on || "",
    starts_at: input.starts_at || "09:00",
    location: String(input.location || "").trim(),
    summary: String(input.summary || "").trim(),
    visibility: input.visibility || "public",
    region: String(input.region || "San Diego County").trim() || "San Diego County",
    url: String(input.url || "").trim(),
  });
}

export function deleteEvent(id) {
  return removeFromList(KEYS.events, SEED_EVENTS, id);
}

export function listPosts() {
  ensureHub();
  return seededList(KEYS.posts, SEED_BBS_POSTS);
}

export function savePost(input = {}) {
  const existing = listPosts().find((item) => item.id === input.id);
  return upsertList(KEYS.posts, SEED_BBS_POSTS, {
    id: input.id || newId(),
    category: input.category || "meetings",
    title: String(input.title || "").trim(),
    body: String(input.body || "").trim(),
    author_name: input.author_name || "Guest",
    author_role: input.author_role || "",
    visibility: input.visibility || "public",
    created_at: existing?.created_at || input.created_at || nowIso(),
    flagged: Boolean(input.flagged),
  });
}

export function deletePost(id) {
  write(
    KEYS.comments,
    listComments().filter((item) => item.post_id !== id)
  );
  write(
    KEYS.likes,
    listLikes().filter((item) => item.post_id !== id)
  );
  return removeFromList(KEYS.posts, SEED_BBS_POSTS, id);
}

export function listComments(postId) {
  ensureHub();
  const all = seededList(KEYS.comments, SEED_BBS_COMMENTS);
  return postId ? all.filter((item) => item.post_id === postId) : all;
}

export function addComment(input = {}) {
  const row = {
    id: newId(),
    post_id: input.post_id,
    author_name: input.author_name || "Guest",
    author_role: input.author_role || "",
    body: String(input.body || "").trim(),
    created_at: nowIso(),
  };
  write(KEYS.comments, [row, ...listComments()]);
  return row;
}

function listLikes() {
  ensureHub();
  return read(KEYS.likes, []) || [];
}

export function likesForPost(postId) {
  return listLikes().filter((item) => item.post_id === postId).length;
}

export function hasLiked(postId, userKey) {
  if (!userKey) return false;
  return listLikes().some((item) => item.post_id === postId && item.user_key === userKey);
}

export function toggleLike(postId, userKey) {
  if (!postId || !userKey) return likesForPost(postId);
  const likes = listLikes();
  const index = likes.findIndex((item) => item.post_id === postId && item.user_key === userKey);
  if (index >= 0) likes.splice(index, 1);
  else likes.push({ post_id: postId, user_key: userKey, created_at: nowIso() });
  write(KEYS.likes, likes);
  return likes.filter((item) => item.post_id === postId).length;
}

export function listSuggestions() {
  ensureHub();
  return (read(KEYS.suggestions, []) || []).sort((a, b) =>
    String(b.created_at).localeCompare(String(a.created_at))
  );
}

export function addSuggestion(input = {}) {
  const row = {
    id: newId(),
    text: String(input.text || "").trim(),
    category: input.category || "other",
    from_name: input.from_name || "Anonymous",
    from_role: input.from_role || "",
    from_user_id: input.from_user_id || "",
    status: "new",
    admin_notes: "",
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  write(KEYS.suggestions, [row, ...listSuggestions()]);
  return row;
}

export function updateSuggestion(id, patch = {}) {
  const list = listSuggestions();
  const index = list.findIndex((item) => item.id === id);
  if (index === -1) return null;
  list[index] = { ...list[index], ...patch, id, updated_at: nowIso() };
  write(KEYS.suggestions, list);
  return list[index];
}

export function recordLoginEvent(input = {}) {
  ensureHub();
  const row = {
    id: newId(),
    user_id: input.user_id || "",
    email: input.email || "",
    name: input.name || "",
    role: input.role || "",
    at: nowIso(),
  };
  const list = [row, ...(read(KEYS.logins, []) || [])].slice(0, 400);
  write(KEYS.logins, list);
  return row;
}

export function listLoginEvents(userId) {
  ensureHub();
  const all = read(KEYS.logins, []) || [];
  return userId ? all.filter((item) => item.user_id === userId) : all;
}

export function listFollowUps() {
  ensureHub();
  return read(KEYS.followups, []) || [];
}

export function getFollowUp(threadId) {
  return listFollowUps().find((item) => item.thread_id === threadId) || null;
}

export function setFollowUp(threadId, input = {}) {
  const list = listFollowUps().filter((item) => item.thread_id !== threadId);
  const row = {
    thread_id: threadId,
    tagged: input.tagged !== false,
    note: String(input.note || "").trim(),
    tagged_by: input.tagged_by || "admin",
    tagged_at: nowIso(),
  };
  if (row.tagged) list.unshift(row);
  write(KEYS.followups, list);
  return row.tagged ? row : null;
}

export function canSeeContent(visibility, role) {
  if (!visibility || visibility === "public") return true;
  if (!role) return false;
  if (role === "admin") return true;
  if (visibility === "pro") return role === "agent" || role === "broker";
  return visibility === role;
}
